import * as Yup from 'yup';
import { getRequiredText } from '../../../../../global/helpers/transformer';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { BeneficiarySettings } from './epi-props';

export const beneficiaryPrimaryStatus = 'primary';
export const beneficiaryContingentStatus = 'contingent';
export const beneficiaryNotDesignatedStatus = 'not-designated';
export const beneficiarySpouseRelationship = 'Spouse';

export const primaryBeneficiariesTotalFieldName = 'primaryBeneficiariesTotal';
export const contingentBeneficiariesTotalFieldName = 'contingentBeneficiariesTotal';

export const shareShareAlikeValue = 'share_alike';
export const shareShareAlikeSurvivorSurvivorsValue = 'share_alike_survivor';

export const cpfLife = 'cpf_life';
export const preretirement = 'preretirement';
export const layLumpSum = 'lay_lump_sum';

export interface Beneficiary {
	id: string;
	firstName: string;
	lastName: string;
	dob: string;
	relationship: string;
	status: string;
	participation: number;
	designationId?: number;
	isOrganization: boolean;
	ineligibleFor?: string[];
	isCurrentSpouse?: boolean;
}

export interface BeneficiaryLandingPageModel {
	showMessage: boolean;
	beneficiaryData: BeneficiaryData;
	lastUpdatedTime: string;
}

export interface BeneficiaryUpdatePageModel {
	primaryBeneficiaries: Beneficiary[];
	contingentBeneficiaries: Beneficiary[];
	clientBeneficiaries: Beneficiary[];
	legalCheck: boolean;
	legalCheck2: boolean;
	primaryShareOption: string;
	contingentShareOption: string;
	addBeneficiariesIds?: string[];
	isMarried: boolean;
}

export interface BeneficiaryUpdatePageOptions {
	beneficiaryStatusPrimaryOptions: any[];
	beneficiaryStatusContingentOptions: any[];
	isPrimaryBeneficiaryEditableWhenMarried: boolean;
	planTitle: string;
	planDescription: string;
	shareOptionPrimaryMin: number;
	shareOptionContingentMin: number;
	guidelineMessage: string;
	guidelineCheckboxMessage: string;
	legalCheckText1?: string;
	legalCheckText2?: string;
	legalCheck2OnlyWhenContingent?: boolean;
	hasPercentages: boolean;
}

export function CalculateTotalParticipation(items: Beneficiary[], status: string): number {
	let total: number = 0;

	if (items?.length > 0) {
		items.forEach(beneficiary => {
			if (beneficiary?.status === status) {
				total += Math.floor(beneficiary.participation);
			}
		});
	}

	return total;
}

export function ParticipationsComplete(items: Beneficiary[], status: string): boolean {
	let total: number = 0;

	if (items?.length > 0) {
		items.forEach(beneficiary => {
			if (beneficiary?.status === status) {
				total += beneficiary.participation > 0 ? 1 : 0;
			}
		});
	}

	return items?.length === total;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function BeneficiaryValidationSchema(
	labelSettings: LabelSettingProps,
	beneficiarySettings: BeneficiarySettings,
	isPrimaryBeneficiaryEditable: boolean,
	legalCheckText2: string,
	shareOptionPrimaryMin: number,
	shareOptionContingentMin: number,
	hasPercentages: boolean,
	legalCheck2OnlyWhenContingent: boolean) {

	const beneficiary = Yup.object().shape({
		status: Yup.string().nullable().required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings.statusColumnLabel))
				.test('designated',
					(value, context) => {
						if (value === beneficiaryNotDesignatedStatus) {							
							return context.createError({								
								message: getRequiredText(labelSettings.requiredFormat, beneficiarySettings.statusColumnLabel),
							});							
						}

						return true;
					}),
		participation: Yup.number().nullable().when([], {
			is: () => hasPercentages,
			then: Yup.number().nullable().min(1, getRequiredText(labelSettings.requiredFormat, beneficiarySettings.percentageColumnLabel)).max(100, beneficiarySettings.participationFormatErrorMessage).required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings.percentageColumnLabel)),
		}),
	});

	return Yup.lazy(() => {
		const schema = Yup.object().shape({
			primaryBeneficiaries: Yup.array().of(beneficiary)
				.test('primary_total',
					(value, context) => {
						if (hasPercentages && value && context && value.length > 0 && ParticipationsComplete(value as any, beneficiaryPrimaryStatus) && isPrimaryBeneficiaryEditable) {
							const total = CalculateTotalParticipation(value as any, beneficiaryPrimaryStatus);

							if (total !== 100) {
								return context.createError({
									path: primaryBeneficiariesTotalFieldName,
									message: beneficiarySettings.participationPrimaryTotalErrorMessage,
								});
							}
						}

						return true;
					}),

			contingentBeneficiaries: Yup.array().of(beneficiary)
				.test('contingent_total',
					(value, context) => {
						if (hasPercentages && value && context && value.length > 0) {
							const total = CalculateTotalParticipation(value as any, beneficiaryContingentStatus);

							if (ParticipationsComplete(value as any, beneficiaryContingentStatus) && total !== 100) {
								return context.createError({
									path: contingentBeneficiariesTotalFieldName,
									message: beneficiarySettings.participationContingentTotalErrorMessage,
								});
							}
						}

						return true;
					}),

			legalCheck: Yup.boolean().nullable().required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings?.legalAcknowledgementTitle)),
			legalCheck2: Yup.boolean().nullable().when(['contingentBeneficiaries'], {
				is: (contingentBeneficiaries: Beneficiary[]) => legalCheckText2 && (!legalCheck2OnlyWhenContingent || (legalCheck2OnlyWhenContingent && contingentBeneficiaries?.length > 0)),
				then: Yup.boolean().nullable().required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings?.legalAcknowledgementTitle)),
			}),
			primaryShareOption: Yup.string().nullable().when(['primaryBeneficiaries'], {
				is: (primaryBeneficiaries: Beneficiary[]) => shareOptionPrimaryMin > -1 && primaryBeneficiaries?.length >= shareOptionPrimaryMin,
				then: Yup.string().nullable().required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings.shareShareAlikeLabel)),
			}),
			contingentShareOption: Yup.string().nullable().when(['contingentBeneficiaries'], {
				is: (contingentBeneficiaries: Beneficiary[]) => shareOptionContingentMin > -1 && contingentBeneficiaries?.length >= shareOptionContingentMin,
				then: Yup.string().nullable().required(getRequiredText(labelSettings.requiredFormat, beneficiarySettings.shareShareAlikeLabel)),
			}),
		});

		return schema;
	});
}

export interface BeneficiaryPerson {
	guid: string;
	gender: string;
	first_name: string;
	middle_name: string;
	last_name: string;
	full_name: string;
	personal_email_address: string;
	office_email_address: string;
	adoption_date: string;
	errors: any,
	name: string;
	adopted: boolean;
	street_address: string;
	location_address: string;
	country: string;
	masked_client_number: string;
	masked_date_of_birth: string;
	masked_ssn: string;
	client_number: string;
	date_of_birth: string;
	ssn: string;
	mailing_address: {
		id: number;
		address1: string;
		address2: string;
		city: string;
		state: string;
		postal_code: string;
		country: string;
		errors: any;
		deactivated: boolean;
	}
}

export interface BeneficiaryOrganization {
	guid: string;
	tax_id: string;
	report_name: string;
	masked_client_number: string;
	client_number: string;
	name: string;
	street_address: string;
	location_address: string;
	country: string;
	business_address: {
		id: number;
		address1: string;
		address2: string;
		city: string;
		state: string;
		postal_code: string;
		country: string;
		deactivated: boolean;
	},
}

export interface BeneficiaryDto {
	beneficiary_designation_type: string;
	percent: number;
	share_option: string;
	relationship_code: string;
	deleted: any;
	errors: any;
	designation_id: number;
	person: BeneficiaryPerson;
	organization?: BeneficiaryOrganization;
}

export interface PlanWithBeneficiary {
	primary: BeneficiaryDto[],
	contingent: BeneficiaryDto[]
}

export interface BeneficiaryData {
	date_last_updated?: string;
	default_spouse: BeneficiaryPerson;
	preretirement: PlanWithBeneficiary;
	cpf_life: PlanWithBeneficiary;
	has_group_life: boolean;
	has_supplemental_life: boolean;
}

export interface BeneficiaryState {
	loaded: boolean;
	beneficiaries: BeneficiaryData;
	createNewBeneficiaryStatus: ApiStatuses;
	relationshipsLoaded: boolean;
	relationships: BeneficiaryRelationshipData[];
}

export interface BeneficiaryRelationshipData {
	guid: string;
	relationship_code: string;
	person: BeneficiaryPerson;
	organization?: BeneficiaryOrganization;
	ineligible_for?: string;
}

export interface Designation {
	percent: number,
	employee_guid: string,
	id: string,
	beneficiary_designation_type: string,
	person_guid?: string;
	organization_guid?: string;
	deleted?: string;
}

export interface PlanUpdate {
	primary_share_option?: string;
	contingent_share_option?: string;
	designations: Designation[];
}

export interface BeneficiariesRequest {
	beneficiaries: {
		preretirement?: PlanUpdate;
		cpf_life?: PlanUpdate;
	};
}

export enum BeneficiaryActions {
	SET_BENEFICIARIES = 'SET_BENEFICIARIES',
	SET_CREATE_BENEFICIARY_STATUS = 'SET_CREATE_BENEFICIARY_STATUS',
	SET_BENEFICIARIES_RELATIONSHIPS = 'SET_BENEFICIARIES_RELATIONSHIPS',
	CLEAR_BENEFICIARIES = 'CLEAR_BENEFICIARIES',	
	CLEAR_BENEFICIARIES_RELATIONSHIPS = 'CLEAR_BENEFICIARIES_RELATIONSHIPS',
}